import { FC, Fragment, useEffect, useState } from 'react';
import { DistributionChannelContent } from '@/models/distributionChannels';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useUpdateDistributionChannelFieldsMutation } from '@/services/endpoints/distributionChannels';
import { StepPageActions, StepPageHeader, StepPageHint } from '@/global/components';
import { AudienceTemplateLinkMessage } from '@/features/audiences';
import { BaseAudience } from '@/models/audiences';
import { Checkbox } from '@/lib';
import { CyclrTemplate } from '@/services/endpoints/integrations';
import { useTranslation } from 'react-i18next';

interface IDestinationStepDataProps {
  selectedAudience: BaseAudience;
  selectedTemplate: CyclrTemplate;
  distributionChannelContent: DistributionChannelContent;
  nextStep: (distributionChannelContent: DistributionChannelContent) => void;
  prevStep: VoidFunction;
  onCancel: VoidFunction;
}

export const DestinationSelectPropertiesStep: FC<IDestinationStepDataProps> = ({
  selectedAudience,
  selectedTemplate,
  distributionChannelContent,
  prevStep,
  nextStep,
  onCancel,
}) => {
  const {t} = useTranslation();
  const workspace = useWorkspace();
  const [selectedFields, setSelectedFields] = useState<{ [key: number]: boolean }>(
    distributionChannelContent.integrationsResult.cyclr.action.fields.reduce(
      (prev, field) => ({
        ...prev,
        [field.fieldId]: true,
      }),
      {}
    )
  );

  const [updateDistributionChannelFields, { isLoading: isUpdateFieldsLoading, isSuccess: isUpdateFieldsSuccess, data: updateFieldsData }] =
    useUpdateDistributionChannelFieldsMutation();

  useEffect(() => {
    if (isUpdateFieldsSuccess && nextStep) {
      nextStep(updateFieldsData);
    }
  }, [isUpdateFieldsSuccess]);

  const changeField = (fieldId: number, checked: boolean) => {
    setSelectedFields({
      ...selectedFields,
      [fieldId]: checked,
    });
  };

  const updateFields = () => {
    const fieldsToIgnore = distributionChannelContent.integrationsResult.cyclr.action.fields
      .filter((field) => !selectedFields[field.fieldId])
      .map((field) => ({
        stepId: field.stepId,
        fieldId: field.fieldId,
        mappingType: 'Ignore',
        value: field.value,
      }));
    if (fieldsToIgnore.length === 0 && nextStep) {
      nextStep(distributionChannelContent);
      return;
    }
    updateDistributionChannelFields({
      workspaceId: workspace.id,
      audienceId: distributionChannelContent.distributionChannel.audienceId,
      distributionChannelId: distributionChannelContent.distributionChannel.id,
      fields: fieldsToIgnore,
    });
  };

  return (
    <Fragment>
      <StepPageHeader
        classNames={{ wrapper: 'pt-8 md:pt-12 lg:pt-16' }}
        title='destinations:selectPropertiesToTransfer_title'
        subtitle='destinations:selectPropertiesToTransfer_description'
      />
      <AudienceTemplateLinkMessage audienceName={selectedAudience.name} templateName={selectedTemplate.Name} />
      <div className='flex flex-wrap gap-16 border-b border-secondary-border pb-8'>
        <StepPageHint title='destinations:edit.step.data.properties' description='destinations:edit.step.data.text' />

        <div className='flex max-w-[50%] flex-col gap-4'>
          {distributionChannelContent.integrationsResult.cyclr.action.fields
            .filter((x) => !x.name.toLowerCase().includes('[script]'))
            .map((field, i, fields) => (
              <Checkbox
                key={field.fieldId}
                label={t(`ALL:destinations.steps.selectAudienceAndDestination.cyclr.${field.name}` as any, field.name)}
                id={field.fieldId.toFixed()}
                description={field.description ? t(`ALL:destinations.steps.selectAudienceAndDestination.cyclr.descriptions.${field.name}` as any, field.description) : undefined}
                disabled={!field.isOptional}
                required={!field.isOptional}
                forceShowingDescription={i !== fields.length - 1}
                checked={selectedFields[field.fieldId]}
                onChange={(e) => changeField(field.fieldId, e.target.checked)}
              />
            ))}
        </div>
      </div>

      <StepPageActions
        classNames={{ wrapper: 'pt-8 grow items-center' }}
        primaryButton={{ text: 'ALL:next', isLoading: isUpdateFieldsLoading, onClick: updateFields }}
        secondaryButton={{ text: 'COMMON:cancel', isLoading: isUpdateFieldsLoading, onClick: onCancel }}
        onBackButtonClick={prevStep}
      />
    </Fragment>
  );
};
